import { Flex, isNotEmpty, LinkButton, StyledText, Types } from "@emonvia/web-components";
import InfoFrame from "@/components/InfoFrame";
import ValidatedTextInput from "@/components/ValidatedTextInput";
import { Button, StyledBlueLinkForTrans } from "@/components/TrackedComponents/reevComponents";
import { Trans } from "react-i18next";
import { ACTIVATION_CODE, LEARN_MORE_ABOUT_REEV_BALANCER_URL_BY_LANGUAGE } from "@/utils/constants";
import { getCurrentLanguage } from "@/utils/i18n";
import { useTranslation } from "@/utils/translations/Translator";
import useAdditionalInfoForm from "@/containers/Steps/AdditionalInfo/hook/useAdditionalInfoForm";
import Frame from "@/components/Frame/Frame";
import { useTheme } from "styled-components";
import { useState } from "react";
import { BoldText } from "@/components/BoldText";
import { DisableReevEmsModal } from "@steps/AdditionalInfo/components/DisableReevEmsModal";
import { Switch } from "@emonvia/ui";
import { trackEvent } from "@/hooks/usePostHog";

const AdditionalInfo = () => {
  const { t } = useTranslation();
  const {
    activationCode,
    maxLoad,
    errorMessage,
    activationCodeExists,
    maxLoadInvalid,
    handleActivationCodeChange,
    handleMaxLoadChange,
    goToNext,
    isLoading,
    emsEnabled,
    setEmsEnabled
  } = useAdditionalInfoForm();
  const theme = useTheme();
  const [showDisableEmsModal, setShowDisableEmsModal] = useState(false);

  const handleEnableEms = () => {
    setEmsEnabled(true);
    trackEvent({ eventName: "Enable Ems" });
  };

  const handleDisableEms = () => {
    setEmsEnabled(false);
    trackEvent({ eventName: "Disable Ems" });
  };

  const handleShowDisableEmsModal = () => {
    setShowDisableEmsModal(true);
  };

  return (
    <Flex flexDirection="column" justifyContent="center" gap={16}>
      <Flex
        padding={[20]}
        style={{
          backgroundColor: theme.colors.frameBodyBackground,
          borderRadius: "10px"
        }}
        flexDirection="column"
      >
        <Flex flexDirection="column" flex={1} gap={12}>
          <StyledText
            text={t("ENTER_ACTIVATION_CODE")}
            type={Types.ns_16_sb}
            color={theme.colors.primaryBlack}
          />
          <ValidatedTextInput
            data-testid="ChargePointInput_ActivationCodeInput"
            title={t("ACTIVATION_CODE")}
            placeholder={t("ENTER_8_DIGITS_ACTIVATION_CODE")}
            value={activationCode}
            onChange={handleActivationCodeChange}
            valid={activationCodeExists}
            error={activationCodeExists === false}
            validators={["removeSpaces"]}
            errorMessage={errorMessage}
          />
        </Flex>
      </Flex>
      <InfoFrame
        description={
          <StyledText
            text={
              <>
                <StyledText
                  text={t("ACTIVATION_CODE_USED_BY_YOUR_CUSTOMER")}
                  style={{ marginBottom: 24 }}
                  type={Types.ns_16_m}
                />
                <Trans
                  i18nKey="YOU_CAN_FIND_ACTIVATION_CODE"
                  components={[
                    <StyledBlueLinkForTrans
                      key={"YOU_CAN_FIND_ACTIVATION_CODE" + "link"}
                      style={{ cursor: "pointer" }}
                      onClick={() => window.open(ACTIVATION_CODE[getCurrentLanguage()])}
                      target="_blank"
                    />
                  ]}
                />
              </>
            }
            type={Types.ns_16_m}
          />
        }
      />
      <Frame>
        <Flex flexDirection="column" style={{ padding: 20 }} gap={12}>
          <Flex flexDirection="row" gap={10}>
            <StyledText type={Types.ns_16_sb} text={t("REEV_EMS")} />
            <Flex
              alignItems="center"
              style={{
                backgroundColor: theme.colors.yellow,
                borderRadius: 6,
                paddingLeft: 10,
                paddingRight: 10
              }}
            >
              <StyledText
                color={theme.colors.primaryBlack}
                text={t("NEW")}
                type={Types.ns_16_b}
                style={{ fontSize: 12 }}
              />
            </Flex>
          </Flex>
          <Flex flexDirection="row" gap={8} alignItems="center">
            <div className="migration flex items-center">
              <Switch
                variant="material2"
                color="blue"
                checked={emsEnabled}
                onCheckedChange={(enabled) =>
                  enabled ? handleEnableEms() : handleShowDisableEmsModal()
                }
              />
            </div>
            <StyledText
              type={Types.ns_16_sb}
              color={theme.colors.primaryBlack}
              text={emsEnabled ? t("ACTIVE") : t("INACTIVE")}
            />
          </Flex>
          <Flex flexDirection="row">
            <StyledText
              text={
                <Trans i18nKey="REEV_ENERGY_MANAGEMENT_SYSTEM_IS_COMPLETELY_FREE">
                  <BoldText />
                </Trans>
              }
              type={Types.ns_16_l}
              color={theme.colors.primaryBlack}
            />
          </Flex>
          <Flex flexDirection="row" gap={12}>
            <LinkButton
              label={t("LEARN_MORE_ABOUT_REEV_EMS")}
              onClick={() => {
                window.open(
                  LEARN_MORE_ABOUT_REEV_BALANCER_URL_BY_LANGUAGE[getCurrentLanguage()],
                  "_blank",
                  "noopener"
                );
              }}
              look="primary"
              fontType={Types.ns_16_sb}
            />
          </Flex>
        </Flex>
        {showDisableEmsModal && (
          <DisableReevEmsModal {...{ handleDisableEms, setShowDisableEmsModal }} />
        )}
      </Frame>
      <Flex
        padding={[20]}
        style={{
          backgroundColor: theme.colors.frameBodyBackground,
          borderRadius: "10px",
          marginTop: 8
        }}
        flexDirection="column"
      >
        <Flex flexDirection="column" flex={1} gap={12}>
          <StyledText
            text={t("MAIN_FUSE_LIMIT_AT_THIS_LOCATION")}
            type={Types.ns_16_sb}
            color={theme.colors.primaryBlack}
          />
          <Flex flexDirection="column" gap={6}>
            <StyledText
              text={t("THIS_VALUE_IS_NEEDED_FOR_SAFE_OPERATION")}
              type={Types.ns_16_l}
              color={theme.colors.primaryBlack}
            />
          </Flex>
          <ValidatedTextInput
            data-testid="ChargePointInput_MaxLoadInput"
            title={t("MAX_LIMIT_IN_A")}
            placeholder={t("ENTER_MAX_4_DIGIT_LIMIT")}
            value={maxLoad}
            onChange={handleMaxLoadChange}
            validators={["keepOnlyNumbers", { key: "limit", options: { limit: 4 } }]}
            valid={isNotEmpty(maxLoad) && maxLoadInvalid === false}
            error={maxLoadInvalid}
            errorMessage={maxLoadInvalid && maxLoad != "" ? t("INVALID_VALUE") : null}
          />
        </Flex>
      </Flex>
      <Button
        label={t("CONFIRM")}
        useNunitoFont
        width="100%"
        margin={[32, 0, 0, 0]}
        disabled={
          activationCodeExists === false || maxLoadInvalid === null || maxLoadInvalid || isLoading
        }
        onClick={goToNext}
      />
    </Flex>
  );
};

export default AdditionalInfo;
